import { getEmbedApiContext } from '../api/context';

/**
 * Browser-side client for VOAI synthesis, routed through admin-api's
 * character-keyed proxy at `/api/tts-voai/{characterId}/synthesize`.
 * The browser passes a `characterId` + text; admin-api reads the
 * persisted `voice.tts.voai.apiKey` and attaches the `X-API-Key`
 * header SERVER-SIDE. The browser NEVER sees the apiKey, the upstream
 * URL (`connect.voai.ai`), or any of the voai-specific synthesis
 * parameters that live on the character record.
 *
 * See the `secret-isolation` + `voai-tts-provider` capability specs.
 */

/** Per-request hard cap to mirror the upstream VOAI limit. */
const VOAI_CHUNK_LIMIT = 1000;

export interface VoaiSynthesizePayload {
  text: string;
  /** Optional override fields used by the admin Voice tab 試聽 button. */
  speaker?: string;
  style?: string;
  version?: 'Classic' | 'Neo' | 'Sota+';
  speed?: number;
  pitch_shift?: number;
  style_weight?: number;
  breath_pause?: number;
  outputFormat?: 'wav' | 'mp3';
  signal?: AbortSignal;
}

/**
 * Synthesise the supplied text via the admin-api character-keyed
 * proxy. Returns a ready-to-play audio Blob (wav or mp3 depending on
 * the persisted `voice.tts.voai.outputFormat`).
 *
 * Belt-and-braces: text > 1000 chars triggers a warn + truncation so a
 * pathological chunk never fails the upstream call. The chunker
 * (`splitForTts`) keeps chunks well below this in practice.
 */
export async function synthesizeVoaiBlob(
  characterId: string,
  payload: VoaiSynthesizePayload,
): Promise<Blob> {
  const ctx = getEmbedApiContext();
  let text = payload.text;
  if (text.length > VOAI_CHUNK_LIMIT) {
    console.warn(`[voai] chunk exceeds 1000-char limit (${text.length}), truncating`);
    text = text.slice(0, VOAI_CHUNK_LIMIT);
  }
  const body: Record<string, unknown> = { text };
  if (payload.speaker) body.speaker = payload.speaker;
  if (payload.style) body.style = payload.style;
  if (payload.version) body.version = payload.version;
  if (payload.speed !== undefined) body.speed = payload.speed;
  if (payload.pitch_shift !== undefined) body.pitch_shift = payload.pitch_shift;
  if (payload.style_weight !== undefined) body.style_weight = payload.style_weight;
  if (payload.breath_pause !== undefined) body.breath_pause = payload.breath_pause;
  if (payload.outputFormat) body.outputFormat = payload.outputFormat;

  const res = await fetch(ctx.apiUrl(`/api/tts-voai/${encodeURIComponent(characterId)}/synthesize`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...ctx.apiAuthHeaders() },
    body: JSON.stringify(body),
    signal: payload.signal,
  });
  if (!res.ok) {
    const errBody = await safeBody(res);
    throw new Error(`VOAI /synthesize HTTP ${res.status}: ${errBody}`);
  }
  return await res.blob();
}

async function safeBody(res: Response): Promise<string> {
  try { return (await res.text()).slice(0, 500); }
  catch { return '<unreadable>'; }
}

export { VOAI_CHUNK_LIMIT };
